import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { EmptyState } from "@/components/common";
import { ListFilter, useListFilter, csvMoney } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, PackagePlus } from "lucide-react";
import MesseBundleCard from "@/components/bundles/MesseBundleCard";
import MesseBundleFormDialog from "@/components/bundles/MesseBundleFormDialog";

const EMPTY = { sku: "", name: "", price_net: "", price_gross: "", tax_rate: 19, components: [] };

export default function MesseBundles({ onChanged }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const canEdit = can(user, "messebundles:edit");
  const canCost = can(user, "finance:view");
  const [rows, setRows] = useState(null);
  const [variants, setVariants] = useState([]);
  const [availMap, setAvailMap] = useState({});
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [settings, setSettings] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => {
    api.get("/messe-bundles", { params: { include_archived: showArchived } }).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/variants").then((r) => setVariants([...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de") || (a.sku || "").localeCompare(b.sku || "", "de")))).catch(() => {});
    api.get("/inventory").then((r) => setAvailMap(Object.fromEntries(r.data.map((x) => [x.variant_id, x.available])))).catch(() => {});
  };
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showArchived]);

  const taxRates = settings?.tax_rates || [0, 7, 19];
  const defaultTax = settings?.default_tax_rate ?? 19;
  const r2 = (n) => Math.round(n * 100) / 100;
  const setPrice = (key, value) => {
    const tax = (Number(f.tax_rate) || 0) / 100;
    const next = { ...f, [key]: value };
    if (key === "price_net") next.price_gross = value !== "" && !isNaN(Number(value)) ? String(r2(Number(value) * (1 + tax))) : "";
    else if (key === "price_gross") next.price_net = value !== "" && !isNaN(Number(value)) ? String(r2(Number(value) / (1 + tax))) : "";
    setF(next);
  };
  const setTax = (v) => { const tax = (Number(v) || 0) / 100; const n = Number(f.price_net); setF({ ...f, tax_rate: Number(v), price_gross: f.price_net !== "" && !isNaN(n) ? String(r2(n * (1 + tax))) : f.price_gross }); };

  const openNew = () => { setEditId(null); setF({ ...EMPTY, tax_rate: defaultTax }); setOpen(true); };
  const openEdit = (b) => {
    setEditId(b.id);
    setF({
      sku: b.sku, name: b.name, price_net: b.price_net ?? "", price_gross: b.price_gross ?? "", tax_rate: b.tax_rate ?? defaultTax,
      components: b.components.map((c) => ({
        component_variant_id: c.missing ? "" : c.component_variant_id,
        quantity: c.quantity, is_free_component: !!c.is_free_component,
      })),
    });
    setOpen(true);
  };

  const dimStr = (v) => { const u = v.dimension_uom || "cm"; if (v.diameter_cm) return `⌀ ${v.diameter_cm} ${u}`; const p = [v.length_cm, v.width_cm, v.height_cm].filter((x) => x != null && x !== ""); return p.length ? `${p.join("×")} ${u}` : ""; };
  const addComp = () => setF({ ...f, components: [...f.components, { component_variant_id: "", quantity: 1, is_free_component: false }] });
  const optionsFor = (i) => { const used = f.components.map((x, j) => (j !== i ? x.component_variant_id : null)).filter(Boolean); return variants.filter((v) => !used.includes(v.id)); };
  const updComp = (i, k, val) => { const c = [...f.components]; c[i] = { ...c[i], [k]: val }; setF({ ...f, components: c }); };
  const rmComp = (i) => setF({ ...f, components: f.components.filter((_, j) => j !== i) });

  const sellable = f.components.length ? Math.max(0, Math.min(...f.components.map((c) =>
    Math.floor((availMap[c.component_variant_id] ?? 0) / (Number(c.quantity) || 1))))) : 0;
  const compCostNet = f.components.reduce((s, c) => { const v = variants.find((x) => x.id === c.component_variant_id); return s + (Number(v?.cost_price) || 0) * (Number(c.quantity) || 1); }, 0);
  const netNum = Number(f.price_net) || 0;
  const grossNum = Number(f.price_gross) || 0;
  const ertragNum = r2(netNum - compCostNet);

  const save = async () => {
    const payload = {
      sku: f.sku, name: f.name, tax_rate: Number(f.tax_rate) || 0,
      price_net: Number(f.price_net) || 0, price_gross: Number(f.price_gross) || 0,
      components: f.components.map((c) => ({ ...c, quantity: Number(c.quantity) || 1 })),
    };
    try {
      if (editId) { await api.put(`/messe-bundles/${editId}`, payload); toast.success(t("messebundle.updated")); }
      else { await api.post("/messe-bundles", payload); toast.success(t("messebundle.created")); }
      setOpen(false); setF(EMPTY); setEditId(null); load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (b) => {
    if (!window.confirm(t("messebundle.confirmDelete", { name: b.name }))) return;
    try { await api.delete(`/messe-bundles/${b.id}`); toast.success(t("messebundle.deleted")); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const archive = async (b) => {
    try { await api.post(`/messe-bundles/${b.id}/archive`); toast.success(t("messebundle.archived")); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const unarchive = async (b) => {
    try { await api.post(`/messe-bundles/${b.id}/unarchive`); toast.success(t("messebundle.unarchived")); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const copyToBundle = async (b) => {
    if (!window.confirm(t("bundle.toBundleConfirm", { name: b.name }))) return;
    try { const r = await api.post(`/messe-bundles/${b.id}/copy-to-bundle`); toast.success(t("bundle.toBundleDone", { sku: r.data.sku })); }
    catch (e) { toast.error(apiError(e)); }
  };

  const compText = (b) => (b.components || []).map((c) => `${c.variant_sku || ""} ${c.variant_name || ""}`).join(" ");
  const filter = useListFilter(rows, { fields: ["name", "sku"], searchOf: compText, storageKey: "messeBundles" });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{t("messebundle.subtitle")}</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="mb-show-archived" onClick={() => setShowArchived((v) => !v)}>{showArchived ? t("messebundle.hideArchived") : t("messebundle.showArchived")}</Button>
          {canEdit && <Button data-testid="new-messe-bundle-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> {t("messebundle.new")}</Button>}
        </div>
      </div>

      <ListFilter filter={filter} testId="messe-bundles" placeholder={t("filter.searchProductSku")}
        exportName="messebundles" exportColumns={[
          { label: "SKU", value: (b) => b.sku },
          { label: t("common.name"), value: (b) => b.name },
          { label: t("common.net"), value: (b) => csvMoney(b.price_net) },
          { label: t("common.gross"), value: (b) => csvMoney(b.price_gross) },
          { label: t("set.vatPercent"), value: (b) => b.tax_rate },
          { label: t("inv.available"), value: (b) => b.sellable ?? 0 },
          { label: t("bundle.components"), value: (b) => (b.components || []).map((c) => `${c.variant_sku || ""}${c.variant_name ? ` · ${c.variant_name}` : ""} x${c.quantity}`).join(" | ") },
        ]} />

      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        rows.length === 0 ? <EmptyState testId="messe-bundles-empty" icon={PackagePlus} title={t("messebundle.emptyTitle")} hint={t("messebundle.emptyHint")} /> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="messe-bundles-list">
            {filter.filtered.map((b) => (
              <MesseBundleCard key={b.id} b={b} canEdit={canEdit} canCost={canCost} onEdit={openEdit} onDelete={del} onCopyToBundle={copyToBundle} onArchive={archive} onUnarchive={unarchive} />
            ))}
          </div>
        )}

      <MesseBundleFormDialog
        open={open} onOpenChange={setOpen} editId={editId} f={f} setF={setF}
        setPrice={setPrice} setTax={setTax} taxRates={taxRates}
        addComp={addComp} updComp={updComp} rmComp={rmComp} optionsFor={optionsFor}
        dimStr={dimStr} availMap={availMap} save={save}
        sellable={sellable} compCostNet={compCostNet} netNum={netNum} grossNum={grossNum} ertragNum={ertragNum}
      />
    </div>
  );
}
